<template>
  <form @submit.prevent="handleSubmit">
    <form-divider>
      <input-field
        v-model="formData.name"
        :errorMessage="v$?.formData['name']?.$errors[0]?.$message"
        :isError="v$?.formData['name']?.$error"
        :label="$t('Integrations.Name')"
        :required="true"
      />
      <checkbox-input
        v-model="formData.config.usePayloadFromResponse"
        :is-checked="formData.config.usePayloadFromResponse"
        label="Use payload from response"
      />
      <input-field
        v-model="formData.config.daprAppId"
        :errorMessage="v$?.formData['config'].daprAppId?.$errors[0]?.$message"
        :isError="v$?.formData['config'].daprAppId?.$error"
        :label="'App ID'"
        :required="true"
      />
      <input-field
        v-model="formData.config.url"
        :errorMessage="v$?.formData['config'].url?.$errors[0]?.$message"
        :isError="v$?.formData['config'].url?.$error"
        :label="'URL'"
        :required="true"
      />
      <div class="py-3">
        <label
          class="whitespace-nowrap text-sm font-medium not-italic text-gray-800"
          >{{ $t("DeviceTypeMetadata.Data")
          }}<span class="pl-0.5 text-orange-600">*</span></label
        >
        <json-editor-vue
          v-model="formData.config.data"
          :main-menu-bar="false"
          :status-bar="false"
          :class="`mt-1 ${v$?.formData['config'].data?.$error && 'error'}`"
          mode="text"
        />
        <span
          v-if="v$?.formData['config'].data?.$error"
          class="mt-2 text-sm text-error"
          >{{ v$?.formData["config"].data?.$errors[0]?.$message }}
        </span>
      </div>
      <multi-json-input
        @input="handleTopicChange"
        :topics="formData.config.topicToChannel"
      />
      <tags-input
        :label="$t('Integrations.Channels')"
        :tags="formData.config.channels"
        @on-tag-mutate="(tags) => (formData.config.channels = tags)"
      />
      <input-field
        v-model="formData.executionOrder"
        :label="$t('Integrations.ExecutionOrder')"
        type="number"
      />
      <checkbox-input
        v-model="formData.isActive"
        :is-checked="formData.isActive"
        :label="$t('Integrations.Active')"
      />
    </form-divider>
    <button-wrapper :is-submit-loading="isSubmitLoading" />
  </form>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import InputField from "../../../core/Components/FormElements/InputField.vue";
import VSelect from "vue-select";
import TheButton from "../../../core/Components/TheButton.vue";
import IntegrationService, {
  CreateEditIntegration,
} from "../Services/IntegrationService";
import CheckboxInput from "@/core/Components/FormElements/CheckboxInput.vue";
import checkIfObjectEmpty, {
  CheckObjectsForPatch,
  ErrorMessageFormatter,
} from "@/core/Services/GlobalFunctions";
import {
  maxLength,
  required,
  requiredIf,
  minValue,
  maxValue,
} from "@vuelidate/validators";
import useValidate from "@vuelidate/core";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import { useToast } from "vue-toastification";
import TagsInput from "@/core/Components/FormElements/TagsInput.vue";
import { specialCharactersValidator } from "@/core/Services/CustomValidators";
//@ts-ignore
import JsonEditorVue from "json-editor-vue";
import MultiJsonInput from "@/core/Components/FormElements/MultiJsonInput.vue";

export default defineComponent({
  components: {
    TagsInput,
    MultiJsonInput,
    JsonEditorVue,
    InputField,
    VSelect,
    TheButton,
    CheckboxInput,
    FormDivider,
    ButtonWrapper,
  },
  props: ["integrationTypeId", "applicationId", "data", "refreshData"],
  data() {
    return {
      v$: useValidate() as any,
      validationErrors: [] as any,
      integrationService: new IntegrationService(),
      isSubmitLoading: false as boolean,
      finalForm: [] as { op: string; path: string; value: any }[],
      toast: useToast(),
      formData: {
        id: this.data.id,
        name: this.data.name,
        executionOrder: this.data.executionOrder,
        integrationTypeId: this.data.integrationTypeId,
        applicationId: this.applicationId,
        isActive: this.data.isActive,
        forwardOnlyPayload: this.data.forwardOnlyPayload,
        config: {
          usePayloadFromResponse: this.data?.config?.usePayloadFromResponse,
          daprAppId: this.data?.config?.daprAppId,
          url: this.data?.config?.url,
          topicToChannel: this.data?.config?.topicToChannel,
          channels: this.data?.config?.channels,
          data: this.data?.config?.data,
        },
      } as CreateEditIntegration,
      integrationFromAPI: {},
    };
  },

  validations() {
    return {
      formData: {
        name: {
          required,
          maxLength: maxLength(50),
          specialCharacters: specialCharactersValidator,
        },
        executionOrder: {
          minValue: minValue(1),
        },
        config: {
          daprAppId: {
            required,
            maxLength: maxLength(1000),
          },
          url: {
            required,
            maxLength: maxLength(1000),
          },
          data: {
            required,
          },
        },
      },
    };
  },
  created() {
    this.integrationFromAPI = Object.assign({}, this.formData);
  },

  watch: {
    applicationId(value) {
      this.formData.applicationId = value;
    },
  },

  methods: {
    async handleSubmit() {
      this.v$.$validate();
      this.validationErrors = this.v$.$errors;
      if (!this.v$.$error) {
        this.isSubmitLoading = true;
        try {
          if (this.formData.config.channels) {
            this.formData.config.channels = this.formData.config.channels.map(
              (channel: string) => {
                if (typeof channel === "string") {
                  return Number(channel.trim());
                }

                return channel;
              }
            );
          }

          if (
            this.formData.config.channels &&
            this.formData.config.channels.length === 0
          ) {
            this.formData.config.channels = null;
          }

          if (checkIfObjectEmpty(this.formData.config.topicToChannel)) {
            this.formData.config.topicToChannel = null;
          }

          if (typeof this.formData.config.data === "string") {
            this.formData.config.data = JSON.parse(this.formData.config.data);
          }

          const integrationPatch = await CheckObjectsForPatch({
            initialObject: this.integrationFromAPI,
            editedObject: this.formData,
          });

          await this.integrationService.editIntegration({
            integrationPatch,
            id: this.data.id,
          });
          this.toast.success((this as any).$t("General.EditedSuccessfully"));
          await this.refreshData();
        } catch (error: any) {
          this.toast.error(ErrorMessageFormatter(error));
        } finally {
          this.isSubmitLoading = false;
        }
      }
    },
    handleTopicChange(value: Record<string, number>) {
      this.formData.config.topicToChannel = value;
    },
  },
});
</script>
<style scoped></style>
